import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import { Permission } from '../../permissions/roles.js';
import { AccountingImportFile } from '../entities/AccountingImportFile.js';
import { AccountingImportMode } from '../entities/AccountingImportMode.js';
import { TaxAdvisorImportInformation } from '../entities/AccountingImportInformation.js';
import { resolveGetAccountingImportFileProblems } from '../server/resolvers/resolveGetAccountingImportFileProblems.js';
import { resolveGetLastTaxAdvisorImportInformation } from '../server/resolvers/resolveGetLastTaxAdvisorImportInformation.js';
import { alreadyWorking, resolveStartAccountingImport } from '../server/resolvers/resolveStartTaxAdvisorImport.js';

/**
 * A subscription, not a mutation: the import runs for minutes in a worker thread of its own and streams its
 * progress lines to the dashboard dialog that started it. Closing that dialog ends the run. Nothing breaks —
 * the import is incremental, a cancelled run leaves complete files behind and the next run picks up the rest —
 * but schools are only queued for resolution at the END of a run: entries a cancelled run imported keep their
 * seeded references until a later run queues their school (its files change again, or "Zuordnungen neu berechnen").
 */
const startTaxAdvisorImport = registerSubscription({
  operationName: 'startTaxAdvisorImport',
  type: () => String,
  params: [{ name: 'mode', type: () => AccountingImportMode }],
  // a full import downloads every file and touches every entry — not something any logged-in user may start
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: (mode: AccountingImportMode) => resolveStartAccountingImport({ mode }),
});

const getLastTaxAdvisorImportInformation = registerMutation({
  operationName: 'getLastTaxAdvisorImportInformation',
  type: () => TaxAdvisorImportInformation,
  middlewares: [authenticatedMiddleware],
  resolve: resolveGetLastTaxAdvisorImportInformation,
});

const getAccountingImportFileProblems = registerQuery({
  operationName: 'getAccountingImportFileProblems',
  type: () => [AccountingImportFile],
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: resolveGetAccountingImportFileProblems,
});

/** Whether an import is running in this server process right now — a second one would import the same files twice. */
const subscribeLockStatusOfImport = registerSubscription({
  operationName: 'subscribeLockStatusOfImport',
  type: () => Boolean,
  middlewares: [checkPermissionMiddleware(Permission.READ_PLATFORM_USAGE, GLOBAL_CONTEXT)],
  resolve: () => () => alreadyWorking.asObservable(),
});

export const TaxAdvisorImportAPI = {
  startImport: startTaxAdvisorImport,
  getLastImportInfo: getLastTaxAdvisorImportInformation,
  getFileProblems: getAccountingImportFileProblems,
  subscribeLock: subscribeLockStatusOfImport,
};
